import { useEffect, useRef, useState } from 'react';
import { SVG, FullscreenMediaPopup } from 'Components';
import { SVG_TYPE, ROUTES } from 'Shared/enums';
import ReactPlayer from 'react-player';
import { generatePath } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

type QuestionType = {
  activeTask: any;
  saveTask: any;
};

const Question = ({ activeTask, saveTask }: QuestionType) => {
  const [isQuestionSaved, setIsQuestionSaved] = useState(activeTask.isSaveTask || activeTask.taskIsSaved);
  const [isMediaPopupOpen, setIsMediaPopupOpen] = useState(false);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const savedQuestionContainerClass = isQuestionSaved ? 'lesson-question__save-label__container--active' : '';
  const savedQuestionIconClass = isQuestionSaved ? 'lesson-question__save-label--active' : '';
  const navigate = useNavigate();

  const getMediaType = (filePath) => {
    if (!filePath) {
      return undefined;
    }

    const filename = filePath.substring(filePath.lastIndexOf('/') + 1);
    if (filename.endsWith('.mp4')) {
      return 'video';
    }

    if (filename.endsWith('.webp')) {
      return 'image';
    }

    return undefined;
  };

  const handleSaveQuestion = () => {
    saveTask({ taskUuid: activeTask.uuid, isQuestionSaved });
    setIsQuestionSaved(!isQuestionSaved);
    activeTask.isSaveTask && navigate({ pathname: generatePath(ROUTES.LEARNING, { submenu: 'save_question' }) });
  };

  const handleOpenMediaPopup = () => {
    if (getMediaType(activeTask?.question?.filePath) !== 'image') {
      return;
    }

    setIsMediaPopupOpen(true);
  };

  useEffect(() => {
    const handleOrientationChange = () => {
      const isMobileViewport = window.innerWidth < 1024;
      if (!isMobileViewport) {
        return;
      }

      const mediaType = getMediaType(activeTask?.question?.filePath);
      const isLandscape = window.innerWidth > window.innerHeight;

      if (mediaType === 'image') {
        setIsMediaPopupOpen(isLandscape);
        return;
      }

      if (mediaType === 'video') {
        const videoElement = videoContainerRef.current?.querySelector('video') as HTMLVideoElement | null;
        if (!videoElement) {
          return;
        }

        if (isLandscape) {
          if (!document.fullscreenElement && videoElement.requestFullscreen) {
            videoElement.requestFullscreen().catch(() => undefined);
          }
        } else if (document.fullscreenElement && document.exitFullscreen) {
          document.exitFullscreen().catch(() => undefined);
        }
      }
    };

    handleOrientationChange();
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [activeTask?.question?.filePath]);

  useEffect(() => {
    setIsMediaPopupOpen(false);
  }, [activeTask?.question?.filePath]);

  const getQuestionMedia = (file) => {
    const filename = file.substring(file.lastIndexOf('/') + 1);
    if (filename.endsWith('.mp4')) {
      return (
        <div className="lesson-question__photo-container lesson-question__photo-container--video" ref={videoContainerRef}>
          <ReactPlayer playing={true} className="video" playsinline={true} url={file} controls={true} width="100%" height="100%" />
          <div className={`lesson-question__save-label__container ${savedQuestionContainerClass}`} onClick={() => handleSaveQuestion()}>
            <SVG className={`lesson-question__save-label ${savedQuestionIconClass}`} type={SVG_TYPE.SAVE_LABEL} />
          </div>
        </div>
      );
    }
    if (filename.endsWith('.webp')) {
      return (
        <div className="lesson-question__photo-container">
          <img className="question__img" src={file} alt="" />
          <span className="lesson-question__zoom" onClick={handleOpenMediaPopup}>
            <SVG type={SVG_TYPE.ZOOM_IN} />
          </span>
          <div className={`lesson-question__save-label__container ${savedQuestionContainerClass}`} onClick={() => handleSaveQuestion()}>
            <SVG className={`lesson-question__save-label ${savedQuestionIconClass}`} type={SVG_TYPE.SAVE_LABEL} />
          </div>
        </div>
      );
    } else {
      return (
        <div className="lesson-question__photo-container lesson-question__photo-container--empty">
          <div className="question__img lesson-question__img--empty">
            <SVG type={SVG_TYPE.NO_CAMERA} />
            Brak mediów do pytania
          </div>
          <div className={`lesson-question__save-label__container ${savedQuestionContainerClass}`} onClick={() => handleSaveQuestion()}>
            <SVG className={`lesson-question__save-label ${savedQuestionIconClass}`} type={SVG_TYPE.SAVE_LABEL} />
          </div>
        </div>
      );
    }
  };

  return (
    <>
      <FullscreenMediaPopup
        isOpen={isMediaPopupOpen}
        src={activeTask?.question?.filePath}
        mediaType={getMediaType(activeTask?.question?.filePath) === 'image' ? 'image' : undefined}
        onClose={() => setIsMediaPopupOpen(false)}
        forceLandscape
        disableVideoControls={false}
        closeOnVideoEnd={false}
      />
      {getQuestionMedia(activeTask.question.filePath)}
      <div className="lesson-question__question">{activeTask.question.question}</div>
    </>
  );
};

export default Question;
